<template>
	<transition name="slide-left-transition">
		<sidebar v-show="isActive" :width="width" :top="top" border>
			<dragger v-model="displayedItems" item-key="id" :disabled="!draggable" @change="handleGroupMoved" handle=".group-move-handler" ghost-class="bg-gray-200" class="py-16">
				<template #item="itemGroup">
					<div class="bg-white mb-8">
						<div v-if="groupProp && itemGroup.element[groupProp]" class="flex justify-between items-center w-full h-full py-8 pl-16 pr-8 border-box">
							<lazy-inline-form-input :disabled="!editable"
													@save="value => handleGroupUpdated(itemGroup.element, value)"
													:value="itemGroup.element[groupProp]">
								<p class="text-sub font-bold leading-tight mb-0">{{ itemGroup.element[groupProp] }}</p>
							</lazy-inline-form-input>

							<div v-if="!itemGroup.element.static" class="flex justify-end mr-4">
								<lazy-dropdown v-if="editable" v-model="itemGroup.element._dropdown" left>
									<icon class="cursor-pointer px-4" color="gray-400" small :icon="icons.options"/>
									<template #menu>
										<nav-list>
											<nav-list-item @click.stop="handleItemAdd(itemGroup.element)">{{ labels.add }}</nav-list-item>
											<nav-list-item color="error" @click.stop="handleGroupDestroy(itemGroup.element)">{{ labels.destroy }}</nav-list-item>
										</nav-list>
									</template>
								</lazy-dropdown>
								<icon v-if="draggable" class="group-move-handler cursor-move px-4" color="gray-400" small :icon="icons.drag"/>
							</div>
						</div>

						<nav-list v-if="itemGroup.element[itemsProp] && itemGroup.element[itemsProp].length" v-model="activeFirstLevelItems">
							<dragger :list="itemGroup.element[itemsProp]" item-key="id" :disabled="!draggable" @change="event => handleItemMoved(itemGroup.element, event)"
									 handle=".item-move-handler"
									 group="items" ghost-class="bg-gray-200">
								<template #item="item">
									<nav-list-item :value="`item_${itemGroup.index}_${item.index}`" :to="item.element.to" :exact="item.element.exact" custom-class="pr-8" :level="1"
												   :active-color="color"
												   :has-children="!!(item.element[subItemsProp] && item.element[subItemsProp].length)">
										<div class="flex justify-between items-center w-full h-full">
											<div class="flex items-center h-full flex-grow-x-1 overflow-x-hidden">
												<icon v-if="item.element.icon" :icon="item.element.icon" small force-size class="mr-16"/>
												<div class="truncate">
													<slot name="item" v-bind:item="item.element"/>
													<span v-if="!$slots.item">{{ item.element.title }}</span>
												</div>
											</div>

											<lazy-dropdown v-if="editable && !itemGroup.element.static" v-model="item.element._dropdown" left>
												<icon class="cursor-pointer px-4" :color="activeFirstLevelItems === `item_${itemGroup.index}_${item.index}` ? 'white' : 'gray-400'"
													  small :icon="icons.options"/>
												<template #menu>
													<nav-list>
														<nav-list-item @click.stop="handleSubItemAdd(itemGroup.element, item.element)">{{ labels.add }}</nav-list-item>
														<nav-list-item @click.stop="handleItemEdit(itemGroup.element, item.element)">{{ labels.edit }}</nav-list-item>
													</nav-list>
												</template>
											</lazy-dropdown>

											<icon v-if="draggable && !itemGroup.element.static" class="item-move-handler cursor-move px-4"
												  :color="activeFirstLevelItems === `item_${itemGroup.index}_${item.index}` ? 'white' : 'gray-400'" small force-size
												  :icon="icons.drag"/>
										</div>

										<template #right>
											<lazy-badge v-if="item.element.badge === 'new'" color="success" rounded-large spaced>
												{{ $t('label.new') }}
											</lazy-badge>
											<lazy-badge v-else-if="item.element.badge === 'beta'" color="secondary" rounded-large spaced>
												{{ $t('label.beta') }}
											</lazy-badge>
										</template>

										<template v-if="item.element[subItemsProp] && item.element[subItemsProp].length" #nested>
											<nav-list v-model="activeSecondLevelItems" class="border-b bg-gray-100">
												<dragger :list="item.element[subItemsProp]" item-key="id" :disabled="!draggable" handle=".sub-item-move-handler"
														 @change="event => handleSubItemMoved(itemGroup.element, item.element, event)"
														 group="sub-items" ghost-class="bg-gray-200">
													<template #item="subItem">
														<nav-list-item :value="`item_${itemGroup.index}_${item.index}-${subItem.index}`" :to="subItem.element.to" small
																	   :active-color="color"
																	   custom-class="pr-8" :exact="subItem.element.exact" :level="2">
															<div class="flex items-center flex-grow-x-1 overflow-x-hidden">
																<div class="flex items-center flex-grow-x-1 overflow-x-hidden">
																	<icon v-if="subItem.element.icon" :icon="subItem.element.icon" x-small force-size class="mr-16" :color="color"
																		  :inverted-color="activeSecondLevelItems === `item_${itemGroup.index}_${item.index}-${subItem.index}`"/>
																	<div class="truncate">
																		<slot name="sub_item" v-bind:item="subItem.element"/>
																		<span v-if="!$slots.sub_item">{{ subItem.element.title }}</span>
																	</div>
																</div>
																<icon v-if="draggable" class="sub-item-move-handler cursor-move px-4"
																	  :color="activeSecondLevelItems === `item_${itemGroup.index}_${item.index}-${subItem.index}` ? 'white' : 'gray-400'"
																	  small force-size :icon="icons.drag"/>
															</div>

															<template #right>
																<lazy-badge v-if="subItem.element.badge === 'new'" color="success" rounded-large spaced>
																	{{ $t('label.new') }}
																</lazy-badge>
																<lazy-badge v-else-if="subItem.element.badge === 'beta'" color="secondary" rounded-large spaced>
																	{{ $t('label.beta') }}
																</lazy-badge>
															</template>

															<template v-if="subItem.element[subItemsProp] && subItem.element[subItemsProp].length" #nested>
																<nav-list v-model="activeThirdLevelItems" class="border-b bg-gray-150">
																	<div v-for="(subSubItem, subSubItemIndex) in subItem.element[subItemsProp]"
																		 :key="item.index + '-' + subItem.index + '-' + subSubItemIndex">
																		<nav-list-item :value="`item_${itemGroup.index}_${item.index}-${subItem.index}-${subSubItemIndex}`"
																					   :to="subSubItem.to" x-small :level="3" custom-class="pl-36 pr-8"
																					   :active-color="color"
																					   :exact="subSubItem.exact">
																			<div class="flex items-center w-full h-full">
																				<icon v-if="subSubItem.icon" :icon="subSubItem.icon" x-small force-size/>
																				<div class="ml-8">
																					<slot name="sub_item" v-bind:item="subSubItem"/>
																					<span v-if="!$slots.sub_item">{{ subSubItem.title }}</span>
																				</div>
																			</div>
																		</nav-list-item>
																	</div>
																</nav-list>
															</template>
														</nav-list-item>
													</template>
												</dragger>
											</nav-list>
										</template>

									</nav-list-item>
								</template>
							</dragger>
						</nav-list>
					</div>
				</template>
			</dragger>
			<slot/>
			<template #footer>
				<slot name="footer"/>
			</template>
		</sidebar>
	</transition>
</template>

<script>
import {mdiDrag} from '@mdi/js';
import dragger from 'vuedraggable'
import {Toggleable} from "@spoferan/nuxt-spoferan/mixins";
import {cloneObject} from "@spoferan/spoferan-ts-core";
import {optionsIcon} from "@spoferan/nuxt-spoferan/icons";

export default {
	name: "DraggableSidebar",

	components: {dragger},

	mixins: [Toggleable],

	emits: ['group-dragged', 'update:items', 'item-dragged', 'sub-item-dragged', 'item-shifted', 'item-add', 'sub-item-add', 'item-edit', 'group-updated', 'group-deleted'],

	props: {
		// The items to display
		items: {
			type: Array,
			required: true,
		},

		groupProp: {
			type: String,
			default: null
		},

		// The name of the prop holding the items in a group
		itemsProp: {
			type: String,
			default: 'items'
		},

		// The name of the prop holding the sub items in an item
		subItemsProp: {
			type: String,
			default: 'items'
		},

		draggable: {
			type: Boolean,
			default: false
		},

		editable: {
			type: Boolean,
			default: false
		},

		width: {
			type: [Number, String],
			default: 300
		},

		// The spacing from the top -> required to be placed below a fixed header
		top: {
			type: [Number, String],
			default: 56
		},

		color: {
			type: String,
			default: null
		}
	},

	data() {
		return {
			displayedItems: this.items,
			activeFirstLevelItems: [],
			activeSecondLevelItems: [],
			activeThirdLevelItems: [],

			icons: {
				drag: mdiDrag,
				options: optionsIcon
			},

			labels: {
				add: this.$t('action.add'),
				edit: this.$t('action.edit'),
				destroy: this.$t('action.destroy'),
			}
		}
	},

	watch: {
		items(items) {
			this.displayedItems = items;
		}
	},

	methods: {

		handleGroupMoved(moveEvent) {
			if (this.draggable) {
				this.$emit('group-dragged', {
					group: moveEvent.moved.element,
					new_position: moveEvent.moved.newIndex + 1,
					old_position: moveEvent.moved.oldIndex + 1,
				})
				this.$emit('update:items', cloneObject(this.displayedItems));
			}
		},

		handleItemMoved(itemGroup, moveEvent) {
			if (this.draggable) {
				if (moveEvent.moved) {
					this.$emit('item-dragged', {
						group: itemGroup,
						item: moveEvent.moved.element,
						new_position: moveEvent.moved.newIndex + 1,
						old_position: moveEvent.moved.oldIndex + 1,
					})
				} else if (moveEvent.added) {
					this.$emit('item-shifted', {
						new_group: itemGroup,
						item: moveEvent.added.element,
						new_position: moveEvent.added.newIndex + 1
					})
				}
			}
			this.$emit('update:items', cloneObject(this.displayedItems));
		},

		handleSubItemMoved(itemGroup, parent, moveEvent) {
			if (this.draggable) {
				if (moveEvent.moved) {
					this.$emit('sub-item-dragged', {
						group: itemGroup,
						parent: parent,
						item: moveEvent.moved.element,
						new_position: moveEvent.moved.newIndex + 1,
						old_position: moveEvent.moved.oldIndex + 1,
					})
				}
			}
			this.$emit('update:items', cloneObject(this.displayedItems));
		},

		handleItemAdd(itemGroup) {
			itemGroup['_dropdown'] = false
			this.$emit('item-add', {
				group: itemGroup
			})
			this.$emit('update:items', cloneObject(this.displayedItems));
		},

		handleSubItemAdd(itemGroup, item) {
			item['_dropdown'] = false
			this.$emit('sub-item-add', {
				group: itemGroup,
				item: item,
			})
			this.$emit('update:items', cloneObject(this.displayedItems));
		},

		handleItemEdit(itemGroup, item) {
			item['_dropdown'] = false
			this.$emit('item-edit', {
				group: itemGroup,
				item: item,
			})
		},

		handleGroupUpdated(itemGroup, value) {
			this.$emit('group-updated', {
				group: itemGroup,
				new_value: value,
				old_value: itemGroup.title,
			})
			itemGroup[this.groupProp] = value
			this.$emit('update:items', cloneObject(this.displayedItems));
		},

		handleGroupDestroy(itemGroup) {
			itemGroup['_dropdown'] = false

			this.$confirm({
				type: 'warning',
				title: this.$t('confirm.section.destroy.title'),
				text: this.$t('confirm.section.destroy.message'),
			}).then(({confirmed}) => {
				if (confirmed) {
					this.$emit('group-deleted', {
						group: itemGroup
					})
					const index = this.displayedItems.indexOf(itemGroup);
					if (index > -1) {
						this.displayedItems.splice(index, 1)
						this.$emit('update:items', cloneObject(this.displayedItems));
					}
				}
			})
		}
	}
}
</script>